import openSocket from "socket.io-client";
import { getBackendUrl } from "../config";
import api from "./api";

// Track all active sockets for hibernation recovery
const activeSockets = new Set();

// ─── feat_346 — re-sync al despertar ────────────────────────────────────────
// Contexto (recon-resync-resume-20260909): al volver de suspensión con el JWT
// vencido (dura 15 min) el server RECHAZA el handshake y hace socket.disconnect()
// → el cliente marca skipReconnect=true y el socket queda MUERTO PARA SIEMPRE.
// Encima nunca hubo re-fetch, así que aunque reconecte la vista queda vieja.
// La secuencia obligatoria en cada despertar es: 1) token fresco → 2) revivir
// sockets → 3) re-fetch. Reconectar con el token vencido REPRODUCE el bug.
const RESYNC_EVENT = "waia:resync";
const RESYNC_DEBOUNCE_MS = 800;
// Backoff propio del revive. socket.io NO reintenta tras "io server disconnect".
// Tope = 4 intentos: si el refresh de 7d también venció, el 401 del interceptor
// de useAuth desloguea. NUNCA reintentar infinito.
const REVIVE_BACKOFF_MS = [1000, 2000, 5000, 10000];
// Un socket que vivió menos que esto y se murió = handshake rechazado, no una
// conexión real que se cayó. No devuelve presupuesto de revive.
const HEALTHY_CONNECTION_MS = 5000;

let hiddenSince = null;
let lastHiddenMs = 0;
let socketDied = false;
let resyncTimer = null;
let resyncTrigger = "reconnect";
let reviveTimer = null;
let reviveAttempt = 0;
let freshTokenPromise = null;

function readToken() {
    const raw = localStorage.getItem("token");
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch (_) {
        return null;
    }
}

// (1) del ciclo token→socket→fetch. Camino de BAJO RIESGO (recon §6.2a): NO
// duplica la lógica de refresh de useAuth (3 ramas de rotación de clave:
// impersonating_refresh_token > wl_refresh_token > cookie jrt — zona caliente
// feat_246/247/248). Dispara un request cualquiera; si el token venció el
// backend responde 403 y el interceptor de useAuth refresca, rota la clave
// correcta y reintenta. Al resolver, localStorage.token está fresco.
// Se dedupea: los ~13 sockets de la pestaña comparten UNA sola verificación.
function ensureFreshToken() {
    if (freshTokenPromise) return freshTokenPromise;
    if (!readToken()) return Promise.resolve();
    freshTokenPromise = api
        .post("/users/heartbeat")
        .catch(() => {})
        .then(() => {
            freshTokenPromise = null;
        });
    return freshTokenPromise;
}

// (3) del ciclo. Señal ÚNICA y global para que las vistas montadas re-fetcheen.
// Debounce global: los ~13 sockets reconectan casi a la vez y tienen que
// colapsar en un solo evento.
function scheduleResync(trigger) {
    resyncTrigger = trigger || resyncTrigger;
    if (resyncTimer) clearTimeout(resyncTimer);
    resyncTimer = setTimeout(() => {
        resyncTimer = null;
        const live = [...activeSockets].filter(s => s.connected).length;
        const hiddenMs = lastHiddenMs;
        const died = socketDied;
        // Si NINGÚN socket quedó vivo, el "connect" que disparó esto fue un
        // handshake rechazado (el server manda CONNECT y recién ahí verifica el
        // JWT y desconecta). Re-fetchear ahí es ruido: el revive va a reintentar
        // y, cuando alguno sobreviva, este evento sale de verdad.
        if (!live) {
            console.info("[resync] skip trigger=%s hiddenMs=%d sockets=0", resyncTrigger, hiddenMs);
            return;
        }
        lastHiddenMs = 0;
        socketDied = false;
        console.info("[resync] trigger=%s hiddenMs=%d sockets=%d", resyncTrigger, hiddenMs, live);
        window.dispatchEvent(
            new CustomEvent(RESYNC_EVENT, {
                detail: { hiddenMs, socketDied: died, trigger: resyncTrigger }
            })
        );
    }, RESYNC_DEBOUNCE_MS);
}

// (2) del ciclo, para el socket que el server MATÓ. UNA secuencia global (no
// una por socket) con backoff propio, porque socket.io ya no lo reintenta.
function scheduleRevive() {
    if (reviveTimer) return;
    if (reviveAttempt >= REVIVE_BACKOFF_MS.length) return;
    const delay = REVIVE_BACKOFF_MS[reviveAttempt];
    reviveAttempt += 1;
    reviveTimer = setTimeout(async () => {
        reviveTimer = null;
        if (!readToken()) return;
        await ensureFreshToken();
        const token = readToken();
        // El interceptor de useAuth borra el token ante un 401 (refresh de 7d
        // vencido) → no hay nada que revivir, la app está deslogueándose.
        if (!token) return;
        let revived = 0;
        activeSockets.forEach(socket => {
            if (!socket.connected) {
                socket.io.opts.query = { token };
                socket.connect();
                revived += 1;
            }
        });
        console.info("[resync] revive attempt=%d sockets=%d", reviveAttempt, revived);
    }, delay);
}

function connectToSocket() {
    const token = localStorage.getItem("token");
    const socket = openSocket(getBackendUrl(), {
        transports: ["websocket", "polling"],
        query: {
            token: JSON.parse(token),
        },
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
    });

    activeSockets.add(socket);

    // Refresh token on each reconnect attempt
    socket.io.on("reconnect_attempt", () => {
        const latest = localStorage.getItem("token");
        if (latest) {
            socket.io.opts.query = { token: JSON.parse(latest) };
        }
    });

    // feat_346 — la señal de re-sync sale SOLO en RE-conexiones. Sin el flag,
    // cada mount de un componente que hace openSocket() genera un connect
    // inicial → un re-fetch de más por lista al entrar a /tickets.
    socket.on("connect", () => {
        socket.__connectedAt = Date.now();
        if (socket.__hasConnectedOnce) scheduleResync("reconnect");
        socket.__hasConnectedOnce = true;
    });

    socket.on("disconnect", (reason) => {
        // Component explicitly disconnected — remove so reconnectAll() ignores it
        if (reason === "io client disconnect") {
            activeSockets.delete(socket);
            return;
        }
        // feat_346 — cualquier caída no pedida por el componente habilita el
        // re-fetch aunque la pestaña no haya estado oculta (corte de red).
        socketDied = true;
        // Una conexión que vivió de verdad y se cayó devuelve el presupuesto de
        // revive; un handshake rechazado (vive milisegundos) NO, o el backoff
        // no llegaría nunca al techo.
        if (Date.now() - (socket.__connectedAt || 0) > HEALTHY_CONNECTION_MS) {
            reviveAttempt = 0;
        }
        // "io server disconnect" = el server nos echó (JWT vencido en el
        // handshake, casi siempre). socket.io marca skipReconnect=true y NO
        // reintenta nunca solo: hay que llamar socket.connect() a mano.
        if (reason === "io server disconnect") scheduleRevive();
    });

    return socket;
}

// Singleton shared across components that must not disconnect the socket
let sharedSocket = null;
export function getSocket() {
    if (!sharedSocket) {
        sharedSocket = connectToSocket();
    }
    return sharedSocket;
}

export default connectToSocket;

// Reconnect sockets that are already disconnected using socket.connect().
// This avoids the race condition that manager.disconnect()+manager.open() causes
// in socket.io-client v3: the old engine closes asynchronously and its onclose()
// calls cleanup() which empties this.subs, wiping the new engine's open callback.
// socket.connect() goes through the Socket layer and handles state correctly.
// Sockets that still think they're connected (TCP silently dead post-hibernate)
// are handled by socket.io's own ping/pong detection which fires shortly after wake.
// feat_346 — ASYNC a propósito: el token se refresca ANTES de iterar. Sin ese
// orden el handshake viaja con el JWT vencido, el server desconecta y los ~13
// sockets quedan muertos sin reintento (§2.4 del recon) = el bug del F5.
async function reconnectAll() {
    await ensureFreshToken();
    activeSockets.forEach(socket => {
        if (!socket.connected) {
            const latest = localStorage.getItem("token");
            if (latest) {
                socket.io.opts.query = { token: JSON.parse(latest) };
            }
            socket.connect();
        }
    });
}

if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", () => {
        if (document.hidden) {
            // feat_346 — se estampa acá para que el gate minHiddenMs del hook
            // pueda distinguir un alt-tab corto de una suspensión larga.
            hiddenSince = Date.now();
            return;
        }
        if (hiddenSince) {
            lastHiddenMs = Date.now() - hiddenSince;
            hiddenSince = null;
        }
        resyncTrigger = "visible";
        // Es async: sin catch, cualquier fallo quedaría como unhandled rejection.
        reconnectAll().catch(() => {});
    });
}

if (typeof window !== "undefined") {
    window.addEventListener("online", () => {
        resyncTrigger = "online";
        reconnectAll().catch(() => {});
    });
}
